import React, {
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  useFormContext,
  useWatch,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import {
  notEmptyString,
} from '@populateui/utils';
import type {
  PatientAuthorizationResponseDTO,
} from 'dtos';
import type {
  PatientForm,
} from 'pages/Dashboard/types/patient';
import type {
  ClaimDataForm,
} from 'pages/Dashboard/pages/Billing/types';
import type {
  VisitNoteServiceCode,
} from 'views/EMR/components/ServiceLookup/type';
import AuthorizationDialog from 'views/EMR/components/Authorizations/Dialog';
import {
  AuthorizationCreationContext,
  AuthorizationCreationTarget,
  OpenAuthorizationCreation,
} from 'pages/Dashboard/pages/Billing/ClaimForm/AuthorizationCreationContext';

type Props = PropsWithChildren<{
  patient?: PatientForm | null;
}>;

export default function AuthorizationCreationProvider({
  children,
  patient,
}: Props) {
  const { getValues, setValue } = useFormContext<ClaimDataForm>();
  const insuranceId = useWatch({ name: 'insurance.insuranceId' }) ?? 0;
  const [open, setOpen] = useState(false);
  const [pendingNumber, setPendingNumber] = useState('');
  const [pendingTarget, setPendingTarget] = useState<AuthorizationCreationTarget | null>(null);

  const clearPendingCreation = useCallback(() => {
    setPendingNumber('');
    setPendingTarget(null);
  }, []);

  const closeDialog = useCallback(() => {
    setOpen(false);
    clearPendingCreation();
  }, [clearPendingCreation]);

  const applySavedAuthorization = useCallback((
    authorizationNumber: string,
    target: AuthorizationCreationTarget,
  ) => {
    if (target.type === 'claim') {
      setValue('patientAuthorizationSelection', {
        authorizationNumber,
      }, { shouldDirty: true });
      return;
    }

    const billingCodes = (getValues('billingCodes') ?? []) as VisitNoteServiceCode[];
    const lineIndex = billingCodes.findIndex((code) => code.__id === target.instanceId);
    if (lineIndex < 0) {
      return;
    }

    const currentLine = billingCodes[lineIndex];
    const nextBillingCodes: VisitNoteServiceCode[] = [
      ...billingCodes.slice(0, lineIndex),
      {
        ...currentLine,
        claimServiceLine: {
          ...currentLine.claimServiceLine,
          authorizationNumber,
        },
      },
      ...billingCodes.slice(lineIndex + 1),
    ];
    setValue('billingCodes', nextBillingCodes, { shouldDirty: true });
  }, [getValues, setValue]);

  const openAuthorizationCreation = useCallback<OpenAuthorizationCreation>((
    authorizationNumber,
    target,
  ) => {
    if (notEmptyString(authorizationNumber)) {
      setPendingNumber(authorizationNumber);
      setPendingTarget(target);
      setOpen(true);
    }
  }, []);

  const onSaved = useCallback((saved: PatientAuthorizationResponseDTO) => {
    const target = pendingTarget;
    clearPendingCreation();
    if (!isNil(target)) {
      applySavedAuthorization(saved.authorizationNumber, target);
    }
  }, [applySavedAuthorization, clearPendingCreation, pendingTarget]);

  const value = useMemo(() => ({
    openAuthorizationCreation,
  }), [openAuthorizationCreation]);

  return (
    <AuthorizationCreationContext.Provider value={value}>
      {children}
      {!isNil(patient) && (
        <AuthorizationDialog
          patient={patient}
          open={open}
          initialAuthorizationNumber={pendingNumber}
          initialInsuranceId={insuranceId > 0 ? insuranceId : undefined}
          onSaved={onSaved}
          onClose={closeDialog}
        />
      )}
    </AuthorizationCreationContext.Provider>
  );
}
